import { useState } from "react";
import {
  Check,
  Infinity as InfinityIcon,
  Loader2,
  Lock,
  PencilLine,
  ShieldAlert,
  Zap,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import {
  switchErrorText,
  useAronWorkModes,
  useSetAronAccess,
  useSetAronAutopilot,
  useSetAronUnlimitedSteps,
} from "../api/aronApi";
import { useAuthStore } from "@/store/auth";
import { useAronUiStore } from "../store/aronUiStore";
import type { AronAccessLevel } from "../types";

interface AccessLevelOption {
  value: AronAccessLevel;
  label: string;
  enabled: boolean;
}

const OPTS: {
  value: AronAccessLevel;
  label: string;
  icon: typeof Lock;
  detail: string;
}[] = [
  {
    value: "read_only",
    label: "Tylko odczyt",
    icon: Lock,
    detail:
      "ARON czyta dane w granicach Twoich uprawnień i nic nie zapisuje w systemie. Warsztat działa normalnie: liczy, przetwarza pliki i oddaje Ci gotowe wyniki.",
  },
  {
    value: "write_no_delete",
    label: "Zapis (bez usuwania)",
    icon: PencilLine,
    detail:
      "ARON może PROPONOWAĆ zapisy — każdy trafia do bazy dopiero po Twoim zatwierdzeniu i z Twoimi uprawnieniami. Usuwanie jest wyłączone zawsze.",
  },
];

/**
 * Access-level selector. For a NEW conversation (no conversationId) it drives the
 * Zustand default for the next thread; for an EXISTING conversation it switches THAT
 * conversation's level via the backend — switchable mid-thread, applies from the next
 * message. Write mode appears only when the backend reports it enabled
 * (ARON_WRITE_ENABLED); even then every write still passes the human-confirmation gate.
 *
 * A chip in the composer, the choice (and what it actually means) in a modal.
 */
export function AronAccessPicker({
  conversationId,
  value,
  autopilot,
  unlimitedSteps,
}: {
  conversationId?: string | null;
  /** The existing conversation's current access level (ignored for a new conversation). */
  value?: AronAccessLevel;
  /** Whether THIS conversation runs in Tryb samodzielny (YOLO). */
  autopilot?: boolean;
  /** Whether THIS conversation has the step limit disabled. */
  unlimitedSteps?: boolean;
} = {}) {
  const storeLevel = useAronUiStore((s) => s.accessLevel);
  const setStoreLevel = useAronUiStore((s) => s.setAccessLevel);
  const storeAutopilot = useAronUiStore((s) => s.autopilotDefault);
  const setStoreAutopilot = useAronUiStore((s) => s.setAutopilotDefault);
  const storeUnlimitedSteps = useAronUiStore((s) => s.unlimitedStepsDefault);
  const setStoreUnlimitedSteps = useAronUiStore(
    (s) => s.setUnlimitedStepsDefault,
  );
  const { data } = useAronWorkModes();
  const setAccess = useSetAronAccess();
  const setAutopilot = useSetAronAutopilot();
  const setUnlimitedSteps = useSetAronUnlimitedSteps();
  const me = useAuthStore((st) => st.user);
  const yoloAllowed = !!(me?.is_ceo || me?.can_use_aron_autopilot);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const failed = (err: unknown) =>
    setError(
      switchErrorText(
        err,
        "Nie udało się zmienić trybu — rozmowa została w poprzednim. Spróbuj ponownie.",
      ),
    );

  const levels = (data?.access_levels as AccessLevelOption[] | undefined) ?? [];
  const writeEnabled = levels.find(
    (l) => l.value === "write_no_delete",
  )?.enabled;

  const chip =
    "inline-flex h-6 shrink-0 items-center gap-1 border px-1.5 text-[11px] transition-colors";

  // Admin disabled write entirely → nothing to toggle; show a locked read-only badge.
  if (!writeEnabled) {
    return (
      <span
        className={`${chip} border-gray-200 text-gray-500 dark:border-gray-700 dark:text-gray-400`}
        title="Zapis (bez usuwania) jest wyłączony przez administratora."
      >
        <Lock size={11} /> Odczyt
      </span>
    );
  }

  const existing = !!conversationId;
  const current: AronAccessLevel = existing
    ? (value ?? "read_only")
    : storeLevel;
  const isYolo = existing ? !!autopilot : storeAutopilot;
  const noStepLimit = existing ? !!unlimitedSteps : storeUnlimitedSteps;
  const isWrite = current === "write_no_delete";

  const toggleStepLimit = (checked: boolean) => {
    if (existing)
      setUnlimitedSteps.mutate({
        conversationId: conversationId!,
        enabled: checked,
      });
    else setStoreUnlimitedSteps(checked);
  };

  // For an existing thread the modal stays open until the server confirms: a
  // refused switch used to roll the chip back silently, and the user's next
  // message went out in the old mode (META-ARON b101478d).
  const choose = (v: AronAccessLevel) => {
    if (!existing) {
      setOpen(false);
      setStoreAutopilot(false);
      if (v !== current) setStoreLevel(v);
      return;
    }
    setError(null);
    const close = () => setOpen(false);
    // Leaving YOLO: any plain level switches autopilot OFF.
    if (isYolo)
      setAutopilot.mutate(
        { conversationId: conversationId!, enabled: false },
        { onSuccess: v === current ? close : undefined, onError: failed },
      );
    if (v !== current)
      setAccess.mutate(
        { conversationId: conversationId!, accessLevel: v },
        { onSuccess: close, onError: failed },
      );
    else if (!isYolo) close();
  };

  const chooseYolo = () => {
    if (!existing) {
      setOpen(false);
      // New conversation: YOLO is a stored DEFAULT — the thread is created with it.
      setStoreLevel("write_no_delete");
      setStoreAutopilot(true);
      return;
    }
    setError(null);
    if (isYolo) {
      setOpen(false);
      return;
    }
    const enableAutopilot = () =>
      setAutopilot.mutate(
        { conversationId: conversationId!, enabled: true },
        { onSuccess: () => setOpen(false), onError: failed },
      );
    if (current !== "write_no_delete")
      setAccess.mutate(
        { conversationId: conversationId!, accessLevel: "write_no_delete" },
        { onSuccess: enableAutopilot, onError: failed },
      );
    else enableAutopilot();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={
          isYolo
            ? "YOLO! ARON zapisuje od razu, bez kart do zatwierdzenia. Kliknij, aby wrócić do trybu z zatwierdzaniem."
            : isWrite
              ? "ARON może proponować zapisy — każdy wymaga Twojego zatwierdzenia. Kliknij, aby zmienić."
              : "ARON tylko czyta dane systemu (warsztat i pliki działają normalnie). Kliknij, aby pozwolić mu proponować zapisy."
        }
        className={`${chip} ${
          isYolo
            ? "border-black bg-black font-bold text-white hover:bg-gray-800"
            : isWrite
              ? "border-red-500 bg-red-600 font-medium text-white hover:bg-red-700"
              : "border-gray-200 text-gray-600 hover:border-red-400 hover:text-red-600 dark:border-gray-700 dark:text-gray-300"
        }`}
      >
        {setAccess.isPending || setAutopilot.isPending ? (
          <Loader2 size={11} className="animate-spin" />
        ) : isYolo ? (
          <Zap size={11} />
        ) : isWrite ? (
          <PencilLine size={11} />
        ) : (
          <Lock size={11} />
        )}
        {isYolo ? "YOLO!" : isWrite ? "Zapis" : "Odczyt"}
      </button>

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setError(null);
        }}
        title="Dostęp ARONa do danych"
        size="md"
        closeOnBackdrop
      >
        {error && (
          <p className="mb-3 border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
            {error}
          </p>
        )}
        <div className="mb-3 flex items-start gap-2 border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-300">
          <ShieldAlert size={14} className="mt-px shrink-0 text-red-600" />
          <span>
            {existing
              ? "Zmiana obowiązuje od następnej wiadomości w tej rozmowie."
              : "Ustawienie dotyczy nowej rozmowy."}{" "}
            W trybach Odczyt/Zapis ARON nie zapisuje nic sam — każdy zapis
            zatwierdzasz Ty. Tryb samodzielny wykonuje zapisy od razu, w jednej
            transakcji z automatycznym wycofaniem przy porażce.
          </span>
        </div>
        <div className="space-y-1">
          {OPTS.map((o) => {
            const Icon = o.icon;
            const isActive = current === o.value && !isYolo;
            return (
              <button
                key={o.value}
                type="button"
                disabled={setAccess.isPending}
                onClick={() => choose(o.value)}
                className={`flex w-full items-start gap-2 border px-3 py-2 text-left transition-colors disabled:opacity-60 ${
                  isActive
                    ? "border-red-500 bg-red-50 dark:bg-red-950/20"
                    : "border-gray-200 hover:border-gray-400 dark:border-gray-700"
                }`}
              >
                <span className="mt-0.5 w-4 shrink-0 text-red-600">
                  {isActive && <Check size={14} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                    <Icon size={13} /> {o.label}
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-500">
                    {o.detail}
                  </span>
                </span>
              </button>
            );
          })}
          {yoloAllowed && (
            <button
              type="button"
              disabled={setAccess.isPending || setAutopilot.isPending}
              onClick={chooseYolo}
              className={`flex w-full items-start gap-2 border px-3 py-2 text-left transition-colors disabled:opacity-60 ${
                isYolo
                  ? "border-red-500 bg-red-50 dark:bg-red-950/20"
                  : "border-gray-200 hover:border-gray-400 dark:border-gray-700"
              }`}
            >
              <span className="mt-0.5 w-4 shrink-0 text-red-600">
                {isYolo && <Check size={14} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                  <Zap size={13} /> YOLO! (tryb samodzielny)
                </span>
                <span className="mt-0.5 block text-xs text-gray-500">
                  ARON wykonuje zapisy OD RAZU, bez kart. Cała tura działa w
                  jednej transakcji: porażka albo decyzja o wycofaniu = baza
                  wraca do stanu sprzed tury; SMS-y i e-maile wychodzą dopiero
                  po udanym zakończeniu.
                </span>
              </span>
            </button>
          )}
        </div>
        {yoloAllowed && (
          <label className="mt-3 flex cursor-pointer items-start gap-2 border border-gray-200 px-3 py-2 transition-colors hover:border-gray-400 dark:border-gray-700">
            <input
              type="checkbox"
              checked={noStepLimit}
              disabled={setUnlimitedSteps.isPending}
              onChange={(e) => toggleStepLimit(e.target.checked)}
              className="mt-0.5 shrink-0 accent-red-600"
            />
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                <InfinityIcon size={13} /> Wyłącz limit kroków
              </span>
              <span className="mt-0.5 block text-xs text-gray-500">
                ARON nie zatrzyma się po osiągnięciu limitu kroków w turze.
                Hamulcem pozostają budżet tokenów i przycisk Stop.{" "}
                {existing
                  ? "Obowiązuje od następnej wiadomości."
                  : "Dotyczy nowej rozmowy."}
              </span>
            </span>
          </label>
        )}
      </Modal>
    </>
  );
}
